import React, { useState, useEffect, useCallback } from "react";
import { 
  Folder, File, Download, Search, FolderOpen, FileText, Image,
  ChevronRight, ChevronDown, Lock, Eye
} from "lucide-react";

const API = getApiUrl();
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Icônes par type de fichier
const FILE_ICONS = {
  ".pdf": "📄",
  ".doc": "📝",
  ".docx": "📝",
  ".xls": "📊",
  ".xlsx": "📊",
  ".ppt": "📽️",
  ".pptx": "📽️",
  ".txt": "📃",
  ".csv": "📊",
  ".jpg": "🖼️",
  ".jpeg": "🖼️",
  ".png": "🖼️",
  ".gif": "🖼️",
  ".webp": "🖼️"
};

export default function GedUserView({ authUser }) {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Charger les dossiers accessibles à l'utilisateur
  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/ged/folders?user_email=${encodeURIComponent(authUser?.email || "")}`);
      if (resp.ok) {
        const data = await resp.json();
        setFolders(data.folders || []);
      }
    } catch (e) {
      console.error("Erreur chargement dossiers:", e);
    }
    setLoading(false);
  }, [authUser?.email]);

  // Charger les documents d'un dossier
  const loadDocuments = useCallback(async (folderId) => {
    try {
      const resp = await authFetch(`${API}/api/ged/documents?folder_id=${folderId}&user_email=${encodeURIComponent(authUser?.email || "")}`);
      if (resp.ok) {
        const data = await resp.json();
        setDocuments(data.documents || []);
      }
    } catch (e) {
      console.error("Erreur chargement documents:", e);
    }
  }, [authUser?.email]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    if (selectedFolder) {
      loadDocuments(selectedFolder.id);
      setSearchResults(null);
    }
  }, [selectedFolder, loadDocuments]);

  // Télécharger un document
  const handleDownloadDocument = async (doc) => {
    window.open(`${API}/api/ged/documents/${doc.id}/download`, "_blank");
  };

  // Recherche intelligente avec Christelle
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const resp = await authFetch(`${API}/api/email/christelle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "search_docs",
          search_query: searchQuery,
          user_email: authUser?.email
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data.result);
        setSelectedFolder(null);
      }
    } catch (e) {
      console.error("Erreur recherche:", e);
    }
    setSearching(false);
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Obtenir le niveau de permission
  const getPermissionLabel = (level) => {
    const labels = {
      read: { text: "Lecture", color: "text-blue-400", icon: Eye },
      write: { text: "Écriture", color: "text-emerald-400", icon: FileText },
      admin: { text: "Admin", color: "text-amber-400", icon: Lock }
    };
    return labels[level] || labels.read;
  };

  return (
    <div className="space-y-4">
      {/* Header avec recherche */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Folder className="w-6 h-6 text-amber-400" />
          <div>
            <h2 className="text-lg font-semibold">Documents (GED)</h2>
            <p className="text-sm text-slate-400">Accédez aux documents partagés</p>
          </div>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Rechercher dans les documents..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg"
            data-testid="ged-search-input"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={searching || !searchQuery.trim()}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-lg flex items-center gap-2"
          data-testid="ged-search-btn"
        >
          {searching ? (
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          Rechercher
        </button>
      </div>

      {/* Contenu principal */}
      <div className="grid grid-cols-12 gap-4">
        {/* Liste des dossiers */}
        <div className="col-span-4 bg-slate-900/50 rounded-xl border border-slate-700 p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
            <FolderOpen className="w-4 h-4 text-amber-400" />
            Mes dossiers ({folders.length})
          </h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full"></div>
            </div>
          ) : folders.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Lock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun dossier accessible</p>
              <p className="text-xs mt-1">Demandez à un administrateur de vous accorder l'accès</p>
            </div>
          ) : (
            <div className="space-y-1">
              {folders.map(folder => {
                const perm = getPermissionLabel(folder.user_permission);
                const PermIcon = perm.icon;
                
                return (
                  <div 
                    key={folder.id}
                    className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      selectedFolder?.id === folder.id 
                        ? "bg-amber-500/20 border-amber-500/50" 
                        : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                    }`}
                    onClick={() => setSelectedFolder(folder)}
                    data-testid={`folder-${folder.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-7 h-7 rounded flex items-center justify-center text-sm"
                        style={{ backgroundColor: (folder.color || "#6b7280") + "20" }}
                      >
                        {folder.icon || "📁"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{folder.name}</p>
                        <div className="flex items-center gap-1 text-xs">
                          <PermIcon className={`w-3 h-3 ${perm.color}`} />
                          <span className={perm.color}>{perm.text}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Contenu du dossier ou résultats de recherche */}
        <div className="col-span-8 bg-slate-900/50 rounded-xl border border-slate-700 p-4">
          {searchResults ? (
            /* Résultats de recherche */
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Search className="w-5 h-5 text-amber-400" />
                  Résultats pour "{searchResults.query}"
                  <span className="text-sm text-slate-400 font-normal">
                    ({searchResults.total || 0} document{(searchResults.total || 0) !== 1 ? "s" : ""})
                  </span>
                </h3>
                <button
                  onClick={() => { setSearchResults(null); setSearchQuery(""); }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  Effacer
                </button>
              </div>

              {searchResults.results?.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun document trouvé</p>
                  <p className="text-sm mt-1">Essayez avec d'autres mots-clés</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {searchResults.results?.map(doc => (
                    <div 
                      key={doc.id}
                      className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-amber-500/50 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {FILE_ICONS[doc.file_type] || "📄"}
                        </span>
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(doc.file_size)} • {doc.file_type}
                          </p>
                          {doc.tags?.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {doc.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="px-1.5 py-0.5 bg-slate-700 rounded text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadDocument(doc)}
                        className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg"
                        title="Télécharger"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : selectedFolder ? (
            /* Contenu du dossier */
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span>{selectedFolder.icon || "📁"}</span>
                  {selectedFolder.name}
                  <span className="text-sm text-slate-400 font-normal">
                    ({documents.length} document{documents.length !== 1 ? "s" : ""})
                  </span>
                </h3>
              </div>

              {selectedFolder.description && (
                <p className="text-sm text-slate-400 mb-4 pb-4 border-b border-slate-700">
                  {selectedFolder.description}
                </p>
              )}

              {documents.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun document dans ce dossier</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {documents.map(doc => (
                    <div 
                      key={doc.id}
                      className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          <span className="text-xl">
                            {FILE_ICONS[doc.file_type] || "📄"}
                          </span>
                          <div>
                            <p className="font-medium text-sm line-clamp-1">{doc.name}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(doc.file_size)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadDocument(doc)}
                          className="p-1.5 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Sélectionnez un dossier pour voir son contenu</p>
              <p className="text-sm mt-1">Ou utilisez la recherche pour trouver un document</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
